/**
 * Remontée UI du garde-fou SKU (cf. skuVariantGuard.ts) pour CatalogDetail et
 * OrderDetail : un toast par lot d'ajout (pas un par ligne), non auto-refermable
 * tant que l'utilisateur ne l'a pas fermé explicitement — une ligne corrigée ou
 * rejetée doit rester visible, pas disparaître après 2,5 s pendant qu'on regarde
 * ailleurs (messika-pwa#7).
 */

import { toast } from 'react-toastify'
import type { SkuVariantDecision } from './skuVariantGuard'

export type SkuVariantGuardEntry = {
   requestedSku: string // SKU reconstruit avec les valeurs d'origine, avant élagage
   keptSku: string // SKU finalement retenu (après élagage)
   decision: SkuVariantDecision
}

type Translate = (key: string) => string
type TranslateWithParams = (key: string, params: unknown[]) => string

const NON_AUTO_CLOSE_TOAST_OPTIONS = {
   position: 'top-right' as const,
   autoClose: false as const,
   closeOnClick: true,
   hideProgressBar: true,
   theme: 'colored' as const
}

const REJECT_REASON_KEY: Record<NonNullable<SkuVariantDecision['reject']>, string> = {
   missing_color: '?Couleur manquante',
   unknown_color: '?Couleur inconnue',
   missing_size: '?Taille manquante',
   unknown_size: '?Taille inconnue'
}

function droppedReasonKey(dropped: SkuVariantDecision['dropped']): string {
   return dropped.includes('color') ? "?Cet article n'a pas de couleur" : "?Cet article n'a pas de taille"
}

/**
 * Affiche (au plus) un toast de correction et un toast de rejet pour le lot
 * d'entrées fourni. Le console.warn par ligne concernée est émis plus tôt,
 * lors de la résolution (cf. useTarifs.sanitizeSkuVariant), là où les listes
 * disponibles sont connues.
 */
export function reportSkuVariantGuard(entries: SkuVariantGuardEntry[], trans: Translate, transWithParams: TranslateWithParams): void {
   const corrected = entries.filter((e) => e.decision.ok && e.decision.dropped.length > 0)
   const rejected = entries.filter((e) => !e.decision.ok)

   if (corrected.length > 0) {
      const body = corrected.map((e) => transWithParams('?SKU errone corrige', [e.requestedSku, e.keptSku, trans(droppedReasonKey(e.decision.dropped))])).join('\n')
      toast.warning(body, NON_AUTO_CLOSE_TOAST_OPTIONS)
   }

   if (rejected.length > 0) {
      const body = rejected.map((e) => transWithParams('?SKU rejete', [e.requestedSku, trans(REJECT_REASON_KEY[e.decision.reject as NonNullable<SkuVariantDecision['reject']>])])).join('\n')
      toast.error(body, NON_AUTO_CLOSE_TOAST_OPTIONS)
   }
}
